//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import FileId from '@models/FileId';
import { InstagramMediaProductType } from '@models/IInstagramMediaItem';
import ItemId from '@models/ItemId';
import MediaType from '@models/MediaType';
import { SocialNetwork } from '@models/SocialNetwork';
import { UniversalItemType } from '@models/UniversalItemType';
import VideoId from '@models/VideoId';

export interface INetworkItem {
  network: UniversalItemType;
  id: ItemId;
  fileId?: FileId;
  thumbnailUrl?: string;
  observationTime: Date;
  postedTime: Date;
  mediaMetadata?: IMediaMetadata;
  mediaType: MediaType;
  caption?: string;
  videoId?: VideoId;
  networkUrl?: string;
  socialNetwork?: SocialNetwork;
  commentCount?: number;
  likeCount?: number;
  videoTitle?: string;
  mediaProductType?: InstagramMediaProductType;
  networkData?: INetworkItemData;
  carouselMedia?: CarouselItem[];
  firstObservationTime?: Date;
  hashtags?: string[];
  mentionedUsers?: string[];
  networkItemId?: ItemId;
  taggedUsers?: string[];
}

export interface IMediaMetadata {
  contentType?: string;
  width: number;
  height: number;
  durationMillis?: number;
  numFrames?: number;
  fileSize?: number;
}

interface INetworkItemData {
  addedToCollectionTimestamp?: Date;
  cameFromSyndication?: boolean;
  captionStatus?: number;
  durationSeconds?: number;
  embedUrl?: string;
  labels?: string[];
  mediaImportData?: IMediaImportNetworkItemData;
  shareCount?: number;
  viewCount?: number;
}

interface CarouselItem {
  fileId?: FileId;
  mediaMetadata?: IMediaMetadata;
  mediaType: 'Image' | 'Video' | 'Unknown';
  specificMediaId: string;
  videoId?: VideoId;
}

export interface IMediaImportNetworkItemData {
  source?: string; // The source url for media import items (e.g. site where Curalate Save button was used)
}

export function isGif<T extends INetworkItem>(item: T): boolean {
  const contentType = item.mediaMetadata?.contentType;
  if (contentType) {
    return contentType === 'image/gif';
  }

  const numFrames = item.mediaMetadata?.numFrames ?? 1;
  const isVideoItem = isVideo(item); // Ignore videos with GIF thumbnails

  return !isVideoItem && numFrames > 1;
}

/**
 * Although it is technically an optional field, networkItem videos should
 * contain a valid videoId, running into an item with a mediaType of `Video`
 * and no valid videoId indicates an error.
 */
export type WithVideo<T extends INetworkItem> = T & {
  mediaType: MediaType.Video;
  videoId: VideoId;
};

export type WithImage<T extends INetworkItem> = T & {
  mediaType: MediaType.Image;
};

export type WithTikTok<T extends INetworkItem> = T & {
  network: UniversalItemType.TikTok;
  networkData?: {
    durationSeconds?: number;
    embedUrl?: string;
  };
};

export type WithYouTube<T extends INetworkItem> = T & {
  network: UniversalItemType.YouTube;
  networkData?: {
    durationSeconds?: number;
    embedUrl?: string;
  };
};

export function isVideo<T extends INetworkItem>(item: T): item is WithVideo<T> {
  return item.mediaType === MediaType.Video && !!item.videoId;
}

export function isImage<T extends INetworkItem>(item: T): item is WithImage<T> {
  return item.mediaType === MediaType.Image;
}

export function isTikTok<T extends INetworkItem>(
  item: T,
): item is WithTikTok<T> {
  return item.network === UniversalItemType.TikTok;
}

export function isYouTube<T extends INetworkItem>(
  item: T,
): item is WithYouTube<T> {
  return item.network === UniversalItemType.YouTube;
}

export type ExistsWithNetworkItem<T extends { networkItem?: INetworkItem }> =
  T & {
    networkItem: INetworkItem;
  };

export type ExistsWithTikTokNetworkItem<
  T extends { networkItem?: INetworkItem },
> = T & {
  networkItem: WithTikTok<INetworkItem>;
};

export type ExistsWithYouTubeNetworkItem<
  T extends { networkItem?: INetworkItem },
> = T & {
  networkItem: WithYouTube<INetworkItem>;
};

/**
 * A helper to check that a given optional entity with a network item (i.e. an MLS media item)
 * contains is defined and has a defined network item on it.
 */
export function isDefinedWithNetworkItem<
  T extends { networkItem?: INetworkItem },
>(item: T | undefined): item is ExistsWithNetworkItem<T> {
  return !!(item && item.networkItem);
}

export function isDefinedWithTikTokNetworkItem<
  T extends { networkItem?: INetworkItem },
>(item: T | undefined): item is ExistsWithTikTokNetworkItem<T> {
  return isDefinedWithNetworkItem(item) && isTikTok(item.networkItem);
}

export function isDefinedWithYouTubeNetworkItem<
  T extends { networkItem?: INetworkItem },
>(item: T | undefined): item is ExistsWithYouTubeNetworkItem<T> {
  return isDefinedWithNetworkItem(item) && isYouTube(item.networkItem);
}
